import { useState, useEffect, useMemo, useRef } from 'react';
import { X, RefreshCw, AlertTriangle } from 'lucide-react';
import type { ProjectoCampaign, ProjectoPLItem, ProjectoInvoice } from '../../types/projecto';
import { PROJECTO_STATUS_CONFIG } from '../../types/projecto';
import {
  projectoCampaignService,
  projectoPLService,
  projectoInvoiceService,
  computeProjectoPLSummary,
} from '../../services/projectoService';
import { ProjectoGeneralTab } from './ProjectoGeneralTab';
import { ProjectoPLTab } from './ProjectoPLTab';
import { ProjectoInvoicesTab } from './ProjectoInvoicesTab';
import { ProjectoActivityTab } from './ProjectoActivityTab';
import { ProjectoCalculatorFooter } from './ProjectoCalculatorFooter';
import { Button } from '../ui/Button';
import { log } from '../../lib/logger';
import { useModal } from '../../contexts/ModalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { reportFirstMissingField } from '../../utils/requiredFields';

type Tab = 'general' | 'pl' | 'invoices' | 'activity';

interface Props {
  project?: ProjectoCampaign | null;
  onClose: () => void;
  onSave: () => void;
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'pl', label: 'P&L' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'activity', label: 'Activity' },
];

export function ProjectoForm({ project, onClose, onSave }: Props) {
  const { toast, showConfirm } = useModal();
  const { t } = useLanguage();
  const isNew = !project;
  const mountedRef = useRef(true);
  const autoCreateCalledRef = useRef(false);

  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [formData, setFormData] = useState<Partial<ProjectoCampaign>>(
    project ?? {
      name: '',
      client_id: null,
      client_name: '',
      brand: '',
      reference: '',
      status: 'Draft',
      start_date: null,
      finish_date: null,
      notes: null,
      drive_link: null,
      budget_enabled: false,
      budget_amount: 0,
      target_margin_pct: 0,
    }
  );
  const [items, setItems] = useState<ProjectoPLItem[]>([]);
  const [invoices, setInvoices] = useState<ProjectoInvoice[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedProjectoId, setSavedProjectoId] = useState<string | null>(project?.id ?? null);
  const [autoCreateError, setAutoCreateError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!project?.id) return;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      projectoPLService.getByProjectoId(project.id),
      projectoInvoiceService.getByProjectoId(project.id),
    ]).then(([plItems, inv]) => {
      if (!mountedRef.current) return;
      setItems(plItems);
      setInvoices(inv);
    }).catch(() => {
      if (!mountedRef.current) return;
      setLoadError('Failed to load project data. Please try again.');
    }).finally(() => {
      if (mountedRef.current) setLoading(false);
    });
  }, [project?.id]);

  const summary = useMemo(() =>
    computeProjectoPLSummary(items, invoices),
    [items, invoices]
  );

  // ── Unsaved changes guard (P&L items and invoices save as you go) ──────
  const serializeFormState = () => JSON.stringify({ ...formData, work_code: undefined });
  const dirtySnapshotRef = useRef<string | null>(null);
  const [snapshotVersion, setSnapshotVersion] = useState(0);
  useEffect(() => {
    dirtySnapshotRef.current = serializeFormState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotVersion]);
  const isDirty = () => dirtySnapshotRef.current !== null && dirtySnapshotRef.current !== serializeFormState();

  const requestClose = async () => {
    if (isDirty()) {
      const discard = await showConfirm({
        title: t('form.discardChanges.title'),
        message: t('form.discardChanges.message'),
        confirmText: t('common.discard'),
        cancelText: t('common.cancel'),
        variant: 'warning',
      });
      if (!discard) return;
    }
    onClose();
  };

  const handleSave = async () => {
    const missingRequired = reportFirstMissingField([
      {
        key: 'projecto-name',
        missing: !(formData.name ?? '').trim(),
        message: 'Preenche o Nome do projeto para poder guardar.',
        reveal: () => setActiveTab('general'),
      },
    ], toast.error);
    if (missingRequired) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (savedProjectoId) {
        await projectoCampaignService.update(savedProjectoId, formData);
      } else {
        const created = await projectoCampaignService.create({
          name: formData.name ?? '',
          client_id: formData.client_id,
          client_name: formData.client_name,
          brand: formData.brand,
          reference: formData.reference,
          status: formData.status,
          start_date: formData.start_date,
          finish_date: formData.finish_date,
          notes: formData.notes,
          drive_link: formData.drive_link,
          budget_enabled: formData.budget_enabled,
          budget_amount: formData.budget_amount,
          target_margin_pct: formData.target_margin_pct,
        });
        setSavedProjectoId(created.id);
        setFormData((f) => ({ ...f, id: created.id }));
      }
      toast.success(t('projecto.saved'));
      setSnapshotVersion(v => v + 1);
      onSave();
    } catch (err) {
      // Surface the real cause instead of failing silently
      const message = err instanceof Error ? err.message : String(err);
      log.error('[ProjectoForm] Failed to save project:', err);
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const retryAutoCreate = async () => {
    setAutoCreateError(false);
    autoCreateCalledRef.current = false;
    doAutoCreate();
  };

  const doAutoCreate = async () => {
    if (autoCreateCalledRef.current) return;
    autoCreateCalledRef.current = true;
    try {
      const created = await projectoCampaignService.create({
        name: 'Untitled Project',
        status: 'Draft',
      });
      if (!mountedRef.current) return;
      setSavedProjectoId(created.id);
      setFormData((f) => ({ ...f, id: created.id, work_code: created.work_code }));
    } catch (err) {
      log.error('[ProjectoForm] Failed to initialize project:', err);
      if (mountedRef.current) {
        setAutoCreateError(true);
        setSaveError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  useEffect(() => {
    if (isNew && !savedProjectoId && !autoCreateCalledRef.current) {
      doAutoCreate();
    }
  }, []);

  const statusCfg = PROJECTO_STATUS_CONFIG[formData.status ?? 'Draft'];

  const showFooter = (activeTab === 'pl' || activeTab === 'invoices') && savedProjectoId;

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <p className="text-sm text-gray-600">{loadError}</p>
          <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      );
    }

    if (!savedProjectoId && activeTab !== 'general') {
      if (autoCreateError) {
        return (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            <p className="text-sm text-gray-600">Failed to initialize project.</p>
            <Button variant="secondary" size="sm" onClick={retryAutoCreate}>
              Retry
            </Button>
          </div>
        );
      }
      return (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          <span className="text-sm">Initializing project...</span>
        </div>
      );
    }

    return (
      <div className="max-w-5xl mx-auto px-6 py-6">
        {activeTab === 'general' && (
          <ProjectoGeneralTab
            data={formData}
            onChange={(u) => setFormData((f) => ({ ...f, ...u }))}
            actualMarginPct={summary.agencyMarginPct}
          />
        )}
        {activeTab === 'pl' && savedProjectoId && (
          <ProjectoPLTab
            projectoId={savedProjectoId}
            items={items}
            onItemsChange={setItems}
          />
        )}
        {activeTab === 'invoices' && savedProjectoId && (
          <ProjectoInvoicesTab
            projectoId={savedProjectoId}
            invoices={invoices}
            onInvoicesChange={setInvoices}
          />
        )}
        {activeTab === 'activity' && savedProjectoId && (
          <ProjectoActivityTab projectoId={savedProjectoId} />
        )}
      </div>
    );
  };

  return (
    <div className="cm-shell cm-shell--fullpage fixed inset-0 z-40 flex flex-col bg-white">
      <div className="cm-header relative px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="cm-title">
              {formData.name && formData.name !== 'Untitled Project' ? formData.name : (isNew ? 'New Projecto' : (formData.name || 'Untitled Project'))}
            </h1>
            {formData.client_name && (
              <p className="cm-subtitle">{formData.client_name}{formData.brand ? ` · ${formData.brand}` : ''}</p>
            )}
          </div>
          {formData.status && (
            <span className={`cm-chip ${statusCfg.bg} ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          )}
          {formData.work_code && (
            <span className="text-xs text-gray-400 font-mono">{formData.work_code}</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button variant="secondary" size="sm" onClick={requestClose}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            isLoading={saving}
            disabled={!(formData.name ?? '').trim()}
            onClick={handleSave}
          >
            {t('common.save', 'Guardar')}
          </Button>
          <button
            onClick={requestClose}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-shrink-0 px-6 border-b border-gray-100 bg-gray-50/50">
        <nav className="cm-tabbar flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`cm-tab ${activeTab === tab.id ? 'cm-tab--active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {saveError && (
        <div className="flex items-start gap-2 px-6 py-3 bg-red-50 border-b border-red-200">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-800">Nao foi possivel guardar as alteracoes.</p>
            <p className="text-xs text-red-700 break-words">{saveError}</p>
          </div>
          <button
            type="button"
            onClick={() => setSaveError(null)}
            className="ml-auto text-red-400 hover:text-red-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="cm-body flex-1 overflow-y-auto bg-gray-50">
        {renderTabContent()}
      </div>

      {showFooter && (
        <ProjectoCalculatorFooter summary={summary} />
      )}
    </div>
  );
}
